import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../services/authService";
import PasswordInput from "../components/PasswordInput";
import { useTranslation } from "react-i18next";
import { validateRequiredField, validateEmailField } from "../utils/validation";
import "./Register.scss";

function buildValidationErrors(form, t) {
  const nextErrors = {};
  const requiredMessage = t("validation.required");
  const emailMessage = t("validation.email");

  validateRequiredField(nextErrors, "name", form.name, requiredMessage);
  validateRequiredField(nextErrors, "surname", form.surname, requiredMessage);
  validateRequiredField(nextErrors, "phone", form.phone, requiredMessage);
  validateRequiredField(nextErrors, "company", form.company, requiredMessage);

  validateEmailField(
    nextErrors,
    "email",
    form.email,
    requiredMessage,
    emailMessage,
  );

  validateRequiredField(nextErrors, "password", form.password, requiredMessage);

  validateRequiredField(
    nextErrors,
    "confirmPassword",
    form.confirmPassword,
    requiredMessage,
  );

  validateRequiredField(
    nextErrors,
    "requestedRole",
    form.requestedRole,
    requiredMessage,
  );

  if (!form.agreed) {
    nextErrors.agreed = t([
      "registerPage.alerts.agreeGdpr",
      "alert_agree_gdpr",
    ]);
  }

  if (
    form.password &&
    form.confirmPassword &&
    form.password !== form.confirmPassword
  ) {
    nextErrors.confirmPassword = t([
      "registerPage.alerts.passwordMismatch",
      "alert_pass_mismatch",
    ]);
  }

  return nextErrors;
}

export default function Register() {
  const navigate = useNavigate();
  const { register, logout } = useAuth();
  const { t, i18n } = useTranslation();

  const [form, setForm] = useState({
    name: "",
    surname: "",
    phone: "",
    company: "",
    contactPerson: "",
    email: "",
    password: "",
    confirmPassword: "",
    requestedRole: "client",
    agreed: false,
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    if (!Object.keys(fieldErrors).some(Boolean)) return;
    setFieldErrors(buildValidationErrors(form, t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);

  const validateForm = () => {
    const nextErrors = buildValidationErrors(form, t);
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await register(
        form.email,
        form.password,
        form.name,
        form.surname,
        form.phone,
        form.company,
        form.contactPerson,
        form.requestedRole,
      );

      await logout();
      navigate("/registered");
    } catch (err) {
      console.error("Registration failed:", err);

      if (err.code === "auth/email-already-in-use") {
        alert(t(["registerPage.alerts.emailUsed", "alert_email_used"]));
      } else {
        alert(
          t(["registerPage.alerts.registrationFailed", "alert_reg_failed"]),
        );
      }
    }
  };

  return (
    <div className="auth-page auth-page--register">
      <div className="auth-page__inner auth-page__inner--register">
        <form
          onSubmit={handleSubmit}
          className="register-form auth-form auth-form--register"
          noValidate
        >
          <h1 className="register-title">
            {t(["registerPage.title", "reg_title"])}
          </h1>

          <div className="form-group">
            <label htmlFor="name">
              <span className="required-star">*</span>
              {t(["registerPage.name", "name"])}
            </label>

            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              aria-invalid={Boolean(fieldErrors.name)}
            />

            {fieldErrors.name && (
              <p className="error-text">{fieldErrors.name}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="surname">
              <span className="required-star">*</span>
              {t(["registerPage.surname", "surname"])}
            </label>

            <input
              id="surname"
              name="surname"
              type="text"
              value={form.surname}
              onChange={handleChange}
              aria-invalid={Boolean(fieldErrors.surname)}
            />

            {fieldErrors.surname && (
              <p className="error-text">{fieldErrors.surname}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              <span className="required-star">*</span>
              {t(["registerPage.phone", "phone"])}
            </label>

            <input
              id="phone"
              name="phone"
              type="text"
              value={form.phone}
              onChange={handleChange}
              aria-invalid={Boolean(fieldErrors.phone)}
            />

            {fieldErrors.phone && (
              <p className="error-text">{fieldErrors.phone}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="company">
              <span className="required-star">*</span>
              {t(["registerPage.company", "company"])}
            </label>

            <input
              id="company"
              name="company"
              type="text"
              value={form.company}
              onChange={handleChange}
              aria-invalid={Boolean(fieldErrors.company)}
            />

            {fieldErrors.company && (
              <p className="error-text">{fieldErrors.company}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="contactPerson">
              {t("registerPage.contactPerson")}
            </label>

            <input
              id="contactPerson"
              name="contactPerson"
              type="text"
              value={form.contactPerson}
              onChange={handleChange}
            />

            <p className="helper-text">
              {t("registerPage.contactPersonHelper")}
            </p>
          </div>

          <div className="form-group">
            <label>
              <span className="required-star">*</span>
              {t(["registerPage.accountType", "account_type"])}
            </label>

            <label className="checkbox-label">
              <input
                type="radio"
                name="requestedRole"
                value="client"
                checked={form.requestedRole === "client"}
                onChange={handleChange}
              />

              {t(["registerPage.roleClient", "role_client"])}
            </label>

            <label className="checkbox-label">
              <input
                type="radio"
                name="requestedRole"
                value="service"
                checked={form.requestedRole === "service"}
                onChange={handleChange}
              />

              {t(["registerPage.roleService", "role_service"])}
            </label>

            {fieldErrors.requestedRole && (
              <p className="error-text">{fieldErrors.requestedRole}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">
              <span className="required-star">*</span>
              {t(["auth.email", "email"])}
            </label>

            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              aria-invalid={Boolean(fieldErrors.email)}
            />

            {fieldErrors.email && (
              <p className="error-text">{fieldErrors.email}</p>
            )}
          </div>

          <PasswordInput
            id="password"
            name="password"
            label={t(["auth.password", "password"])}
            value={form.password}
            onChange={handleChange}
            ariaInvalid={Boolean(fieldErrors.password)}
          />

          {fieldErrors.password && (
            <p className="error-text">{fieldErrors.password}</p>
          )}

          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            label={t(["registerPage.confirmPassword", "confirm_password"])}
            value={form.confirmPassword}
            onChange={handleChange}
            ariaInvalid={Boolean(fieldErrors.confirmPassword)}
          />

          {fieldErrors.confirmPassword && (
            <p className="error-text">{fieldErrors.confirmPassword}</p>
          )}

          <p className="required-info">
            <span className="required-star">*</span>
            {t(["registerPage.requiredFields", "required_fields"])}
          </p>

          <label className="checkbox-label checkbox-label--gdpr">
            <input
              type="checkbox"
              name="agreed"
              checked={form.agreed}
              onChange={handleChange}
            />

            <span className="checkbox-label__text">
              <span className="checkbox-label__lead">
                {t(["registerPage.agreeGdpr", "agree_gdpr"])}
              </span>{" "}
              <a
                href="https://www.codaco.cz/bezpecnost-a-ochrana-osobnich-udaju"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(["registerPage.agreeGdprLink", "agree_gdpr_link"])}
              </a>
            </span>
          </label>

          {fieldErrors.agreed && (
            <p className="error-text">{fieldErrors.agreed}</p>
          )}

          <button type="submit">
            {t(["auth.registerButton", "btn_register"])}
          </button>
        </form>
      </div>
    </div>
  );
}
